import { courseApi } from '@/api/course.api';
import { CourseTypeOption } from '@/jbi-shared/dto/jtms.dtos';
import {
  GroupLevelAttributeContactsType,
  GroupLevelAttributeOption
} from '@/jbi-shared/types/jaas-group-level-attributes.types';
import {
  CourseModuleStatus,
  CoursePackageStatus
} from '@/jbi-shared/types/jtms.types';
import { Store } from 'vuex';
import {
  CourseState,
  CreateCourseMasterDTO,
  FilteredCoursePayload,
  UpdateCourseApiDTO,
  UpdateCourseTypeApiDTO
} from './course.types';

export const courseActions = {
  async getCoursesCountForEachStatus(store: Store<CourseState>) {
    try {
      store.commit('GET_COURSE_COUNT_FOR_EACH_STATUS_LOADING');
      const { data } = await courseApi.getCoursesCountForEachStatus();
      store.commit('GET_COURSE_COUNT_FOR_EACH_STATUS_SUCCESS', data);
    } catch (error) {
      store.commit('GET_COURSE_COUNT_FOR_EACH_STATUS_ERROR', error);
    }
  },
  async getCourseTypes(store: Store<CourseState>) {
    try {
      store.commit('GET_COURSE_TYPES_LOADING');
      const { data } = await courseApi.getCourseTypes();
      store.commit('GET_COURSE_TYPES_SUCCESS', data);
    } catch (error) {
      store.commit('GET_COURSE_TYPES_ERROR', error);
    }
  },
  async getCourses(store: Store<CourseState>, payload: FilteredCoursePayload) {
    try {
      store.commit('GET_COURSES_LOADING');

      const { data } = await courseApi.getCourses(payload);

      store.commit('GET_COURSES_SUCCESS', data);

      return data;
    } catch (error) {
      store.commit('GET_COURSES_ERROR', error);
      throw error;
    }
  },

  async getCourseBySlug(store: Store<CourseState>, slug: string) {
    try {
      store.commit('GET_COURSE_BY_SLUG_LOADING');

      const { data } = await courseApi.getCourseBySlug(slug);

      store.commit('GET_COURSE_BY_SLUG_SUCCESS', data);
      return data;
    } catch (error) {
      store.commit('GET_COURSE_BY_SLUG_ERROR', error);
      throw error;
    }
  },

  async getFullCourseDetailsBySlug(store: Store<CourseState>, slug: string) {
    try {
      store.commit('GET_FULL_COURSE_DETAILS_BY_SLUG_LOADING');

      const { data } = await courseApi.getFullCourseDetailsBySlug(slug);

      store.dispatch(
        'shortCourseCheckout/assignPackageSelectionsFromCoursePackages',
        data.coursePackages,
        { root: true }
      );

      store.commit('GET_FULL_COURSE_DETAILS_BY_SLUG_SUCCESS', data);
      return data;
    } catch (error) {
      store.commit('GET_FULL_COURSE_DETAILS_BY_SLUG_ERROR', error);
      throw error;
    }
  },

  async getCourseDetails(store: Store<CourseState>, id: number) {
    try {
      store.commit('GET_COURSE_DETAILS_LOADING');

      const { data } = await courseApi.getCourseDetails(id);

      store.commit('GET_COURSE_DETAILS_SUCCESS', data);

      // Assign existing course modules on course details fetch
      store.dispatch('storeSelectedCourseType', {
        id: data.courseType.id,
        name: data.courseType.name
      });

      store.dispatch('updateHasQuestionnaire', data.hasQuestionnaire);
      store.dispatch('updateIsFreeCourse', data.isFreeCourse);

      if (data.courseModules && data.courseModules.length > 0) {
        const hasNonDefaultCourseModules: boolean = data.courseModules.some(
          (cm) => cm.status !== CourseModuleStatus.DEFAULT
        );
        store.dispatch('updateCourseHasModules', hasNonDefaultCourseModules);

        store.dispatch(
          'courseModule/assignExistingCourseModules',
          data.courseModules,
          { root: true }
        );
      }

      if (data.coursePackages && data.coursePackages.length > 0) {
        const hasNonDefaultCoursePackages: boolean = data.coursePackages.some(
          (cp) => cp.status !== CoursePackageStatus.DEFAULT
        );
        store.dispatch('updateCourseHasPackages', hasNonDefaultCoursePackages);

        store.dispatch(
          'coursePackage/assignExistingCoursePackages',
          data.coursePackages,
          { root: true }
        );
      }

      return data;
    } catch (error) {
      store.commit('GET_COURSE_DETAILS_ERROR', error);
      throw error;
    }
  },

  async getDefaultCourseLevelAttributes(store: Store<CourseState>) {
    try {
      store.commit('GET_DEFAULT_COURSE_LEVEL_ATTRIBUTES_LOADING');

      const { data } = await courseApi.getDefaultCourseLevelAttributes();
      store.commit('GET_DEFAULT_COURSE_LEVEL_ATTRIBUTES_SUCCESS', data);
    } catch (error) {
      store.commit('GET_DEFAULT_COURSE_LEVEL_ATTRIBUTES_ERROR', error);
      throw error;
    }
  },

  async getRequiredPublicCourseLevelAttributes(store: Store<CourseState>) {
    try {
      store.commit('GET_REQUIRED_PUBLIC_COURSE_LEVEL_ATTRIBUTES_LOADING');

      const { data } = await courseApi.getRequiredPublicCourseLevelAttributes();
      store.commit('GET_REQUIRED_PUBLIC_COURSE_LEVEL_ATTRIBUTES_SUCCESS', data);
    } catch (error) {
      store.commit('GET_REQUIRED_PUBLIC_COURSE_LEVEL_ATTRIBUTES_ERROR', error);
      throw error;
    }
  },

  async createNewCourse(
    store: Store<CourseState>,
    payload: CreateCourseMasterDTO
  ) {
    try {
      store.commit('CREATE_NEW_COURSE_LOADING');

      const { data } = await courseApi.createNewCourse(payload);

      store.commit('CREATE_NEW_COURSE_SUCCESS', data.courseId);
    } catch (error) {
      store.commit('CREATE_NEW_COURSE_ERROR', error);
      throw error;
    }
  },

  async createDraftCourse(
    store: Store<CourseState>,
    payload: CreateCourseMasterDTO
  ) {
    try {
      store.commit('CREATE_DRAFT_COURSE_LOADING');

      const { data } = await courseApi.createDraftCourse(payload);

      store.commit('CREATE_DRAFT_COURSE_SUCCESS', data.courseId);
    } catch (error) {
      store.commit('CREATE_DRAFT_COURSE_ERROR', error);
      throw error;
    }
  },

  async updateCourse(store: Store<CourseState>, payload: UpdateCourseApiDTO) {
    try {
      store.commit('UPDATE_COURSE_LOADING');

      const { data } = await courseApi.updateCourse(payload);

      store.commit('UPDATE_COURSE_SUCCESS', data);

      return data;
    } catch (error) {
      store.commit('UPDATE_COURSE_ERROR', error);
      throw error;
    }
  },

  async updateCourseTypes(
    store: Store<CourseState>,
    payload: UpdateCourseTypeApiDTO
  ) {
    try {
      store.commit('UPDATE_COURSE_TYPE_LOADING');

      await courseApi.updateCourseType(payload);

      store.commit('UPDATE_COURSE_TYPE_SUCCESS');
    } catch (error) {
      store.commit('UPDATE_COURSE_TYPE_ERROR', error);
      throw error;
    }
  },

  storeLocationAttributeConfig(
    store: Store<CourseState>,
    payload: GroupLevelAttributeOption[]
  ) {
    store.commit('STORE_LOCATION_ATTRIBUTE_CONFIG', payload);
  },

  storeListOfContacts(
    store: Store<CourseState>,
    payload: GroupLevelAttributeContactsType[]
  ) {
    store.commit('STORE_LIST_OF_CONTACTS', payload);
  },

  updateCourseHasModules(store: Store<CourseState>, payload: boolean) {
    store.commit('UPDATE_COURSE_HAS_MODULES', payload);
  },

  updateCourseHasPackages(store: Store<CourseState>, payload: boolean) {
    store.commit('UPDATE_COURSE_HAS_PACKAGES', payload);
  },

  updateHasQuestionnaire(store: Store<CourseState>, payload: boolean) {
    store.commit('UPDATE_HAS_QUESTIONNAIRE', payload);
  },

  // If course is free:
  // 1. Set currency and amount fields to null and 0 respectively
  // 2. Revalidate course packages while skipping those fields
  updateIsFreeCourse(store: Store<CourseState>, isFree: boolean) {
    store.commit('UPDATE_HAS_FREE_COURSE', isFree);
    store.dispatch('coursePackage/toggleCurrencyAndAmountFields', isFree, {
      root: true
    });
    store.dispatch('coursePackage/validateAllCoursePackages', undefined, {
      root: true
    });
  },

  storeSelectedCourseType(
    store: Store<CourseState>,
    payload: CourseTypeOption
  ) {
    store.commit('UPDATE_SELECTED_COURSE_TYPE', payload);
  },

  setCurrentActiveTab(store: Store<CourseState>, payload: string) {
    store.commit('SET_CURRENT_ACTIVE_TAB', payload);
  },

  setShouldCreateWithLicense(store: Store<CourseState>, payload: boolean) {
    store.commit('SET_SHOULD_CREATE_WITH_LICENSE', payload);
  },

  storeCourseSize(store: Store<CourseState>, payload: number) {
    store.commit('STORE_COURSE_SIZE', payload);
  },

  storeStartDate(store: Store<CourseState>, payload: string) {
    store.commit('STORE_START_DATE', payload);
  },

  storeEndDate(store: Store<CourseState>, payload: string) {
    store.commit('STORE_END_DATE', payload);
  }
};
